import i18n from 'i18n';
import {
  DOMAIN_REGEX,
  EMAIL_VALIDATION_REGEX,
  SUB_DOMAIN_REGEX_PREFIX,
  YOUTUBE_LINK_REGEX
} from 'shared/constants/validation-regex';
import { InfoType } from 'shared/interfaces/tools/CollectLeadsInfo';
import { TranscriptionConfiguration } from 'shared/interfaces/Transcription';
import { VoiceConfiguration } from 'shared/interfaces/Voice';

export const MIN_LENGTH = 2;
export const MAX_LENGTH = 50;
export const MAX_PASSWORD_STRENGTH = 100;
export const MEDIUM_PASSWORD_STRENGTH = 50;
export const PASSWORD_MIN_LENGTH = 8;
export const CUSTOM_PROMPT_MAX_LENGTH = 10_000;
export const FOLLOW_UP_INSTRUCTIONS_MAX_LENGTH = 500;
export const CANCELATION_COMMENT_MAX_LENGTH = 1000;
export const SMART_ASSISTANT_NAME_MAX_LENGTH = 30;
export const SMART_ASSISTANT_WELCOME_MESSAGE_MAX_LENGTH = 180;
export const DISCORD_MAX_LENGTH_COMMAND = 20;
export const TEAM_NAME_MAX_LENGTH = 50;
export const TEAM_MAX_NUM_MEMBERS = 100;
export const MAX_TEAMS_PER_RESOURCE = 10;
export const MAX_TEAMS_PER_SMART_ASSISTANT = 10;
export const DEFAULT_MAX_LENGTH = 500;
export const MAX_CALL_TRANSFER_RULES = 5;

export const requiredInputValidator = (value: string | number, forbiddenValue?: string | number) => {
  if (typeof value === 'number') return value ? null : i18n.t('FORM_VALIDATION.REQUIRED');
  if (forbiddenValue && value.trim() === forbiddenValue) {
    return i18n.t('FORM_VALIDATION.REQUIRED');
  }
  return value.trim().length === 0 ? i18n.t('FORM_VALIDATION.REQUIRED') : null;
};

export const emailValidator = (value: string) =>
  !EMAIL_VALIDATION_REGEX.test(value) ? i18n.t('FORM_VALIDATION.EMAIL') : null;

export const domainValidator = (value: string) =>
  !DOMAIN_REGEX.test(value) ? i18n.t('FORM_VALIDATION.INVALID_DOMAIN') : null;

export const subDomainPrefixValidator = (value: string) =>
  !SUB_DOMAIN_REGEX_PREFIX.test(value) ? i18n.t('FORM_VALIDATION.INVALID_SUB_DOMAIN') : null;

export const minNumberValidator = (value: number, min: number) =>
  value < min ? i18n.t('FORM_VALIDATION.MIN_NUMBER', { MIN: min }) : null;

export const maxNumberValidator = (value: number, max: number) =>
  value > max ? i18n.t('FORM_VALIDATION.MAX_NUMBER', { MAX: max }) : null;

export const minLengthValidator = (value: string, minLength = MIN_LENGTH) =>
  value.trim().length < minLength ? i18n.t('FORM_VALIDATION.MIN_LENGTH', { MIN_LENGTH: minLength }) : null;

export const maxLengthValidator = (value: string, maxLength = MAX_LENGTH) =>
  value.trim().length > maxLength ? i18n.t('FORM_VALIDATION.MAX_LENGTH', { MAX_LENGTH: maxLength }) : null;

export const maxStringArrayLengthValidator = (value: string[], MAX_LENGTH = 50) =>
  value.length > MAX_LENGTH ? i18n.t('FORM_VALIDATION.MAX_LENGTH', { MAX_LENGTH }) : null;

export const nonEmptyArrayValidator = (value: unknown[]) =>
  value.length === 0 ? i18n.t('FORM_VALIDATION.REQUIRED') : null;

export const validateStrictArrayCount = (value: string[], validCounts: number[]) =>
  validCounts.includes(value.length) ? null : i18n.t('FORM_VALIDATION.INVALID_ARRAY_COUNT');

export const passwordStrengthValidator = (value: string, maxStrength = MAX_PASSWORD_STRENGTH) => {
  const strength = getPasswordStrength(value);
  return strength !== maxStrength ? i18n.t('FORM_VALIDATION.PASSWORD.INVALID') : null;
};

export const numericStringValidator = (code: string) => {
  const regex = /^\d+$/;
  return regex.test(code);
};

export const requirementDateValidator = (date: Date | null) => {
  if (date === null) {
    return i18n.t('FORM_VALIDATION.REQUIRED');
  } else if (date < new Date()) {
    return i18n.t('FORM_VALIDATION.DUE_DATE');
  }
  return null;
};

export const inputMaxLengthValidator = (value: string, maxValue: number) => {
  if (value.length > maxValue) {
    return i18n.t('FORM_VALIDATION.MAX_LENGTH', { MAX_LENGTH: maxValue });
  }
  return null;
};

export const uniqueEmailValidator = (email: string, emails: string[]) => {
  const lowercaseEmail = email.trim().toLowerCase();
  const lowercaseEmailSet = new Set(emails.map((e) => e.trim().toLowerCase()));

  return lowercaseEmailSet.has(lowercaseEmail) ? i18n.t('FORM_VALIDATION.UNIQUE_EMAIL') : null;
};

export const uniqueArrayValidator = (values: string[], evaluatedValue: string) => {
  // If value has a different index than its valueIndex, it is a duplicate
  const duplicateValues = values.filter((value, valueIndex) => values.indexOf(value) !== valueIndex);
  return duplicateValues.includes(evaluatedValue) ? i18n.t('FORM_VALIDATION.UNIQUE_ARRAY') : null;
};

export const awsResetPasswordCodeValidator = (code: string) => {
  return code.length !== 6 || !numericStringValidator(code)
    ? i18n.t('FORM_VALIDATION.AWS_VERIFICATION_CODE.ERROR_INVALID_VERIFICATION_CODE')
    : null;
};
export const confirmPasswordValidator = (password: string, confirmPassword: string) =>
  password !== confirmPassword ? i18n.t('FORM_VALIDATION.PASSWORD.NO_MATCH') : null;

export const PASSWORD_REQUIREMENTS = [
  { regex: /(?=.*\d)/, errorMessage: i18n.t('FORM_VALIDATION.PASSWORD.INCLUDE_NUMBER') },
  { regex: /(?=.*[a-z])/, errorMessage: i18n.t('FORM_VALIDATION.PASSWORD.INCLUDE_LOWER_CASE') },
  { regex: /(?=.*[A-Z])/, errorMessage: i18n.t('FORM_VALIDATION.PASSWORD.INCLUDE_UPPERCASE') },
  { regex: /(?=.*[$&+,:;=?@#|'<>.^*()%!-])/, errorMessage: i18n.t('FORM_VALIDATION.PASSWORD.INCLUDE_SPECIAL_SYMBOL') },
  { regex: /(?=^.{8,}$)/, errorMessage: i18n.t('FORM_VALIDATION.PASSWORD.MIN_LENGTH', { PASSWORD_MIN_LENGTH }) }
];

export const getPasswordStrength = (password: string) => {
  let unFulfilledRequirements = 0;
  PASSWORD_REQUIREMENTS.forEach((requirement) => {
    if (!requirement.regex.test(password)) {
      unFulfilledRequirements += 1;
    }
  });

  return Math.max(
    MAX_PASSWORD_STRENGTH - (MAX_PASSWORD_STRENGTH / (PASSWORD_REQUIREMENTS.length + 1)) * unFulfilledRequirements,
    10
  );
};

export const youtubeVideoUrlValidator = (youtubeVideoUrl: string) => {
  return YOUTUBE_LINK_REGEX.test(youtubeVideoUrl) ? null : i18n.t('FORM_VALIDATION.YOUTUBE_VIDEO_URL');
};

export const validateUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:') {
      if (
        process.env.NODE_ENV !== 'development' &&
        (parsedUrl.hostname.includes('localhost') ||
          parsedUrl.hostname.startsWith('127.') ||
          parsedUrl.hostname.startsWith('192.168.'))
      ) {
        return i18n.t('FORM_VALIDATION.INVALID_URL');
      }
      return null;
    } else {
      return i18n.t('FORM_VALIDATION.INVALID_URL');
    }
  } catch {
    return i18n.t('FORM_VALIDATION.INVALID_URL');
  }
};

export const validateSubLinkUrl = (url: string, baseUrl: URL) => {
  try {
    const subLinkUrl = new URL(url);
    if (subLinkUrl.hostname !== baseUrl.hostname) return i18n.t('FORM_VALIDATION.INVALID_SUB_LINK_URL');
    return null;
  } catch {
    return i18n.t('FORM_VALIDATION.INVALID_URL');
  }
};

export const validateHexColor = (color: string) => {
  const regex = /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
  return regex.test(color) ? null : i18n.t('FORM_VALIDATION.INVALID_HEX_COLOR');
};

export const validateHexColors = (colors: string[]) => {
  return colors.every((color) => validateHexColor(color) === null)
    ? null
    : i18n.t('FORM_VALIDATION.INVALID_HEX_COLORS');
};

export const characterCountColor = (limit: number, characterCount: number) => {
  return characterCount > limit ? 'red' : 'dimmed';
};

export const validateDiscordInviteLink = (inviteLink: string) => {
  return /^https?:\/\/discord(?:\.gg|app\.com\/invite)\/\w{2,255}$/.test(inviteLink)
    ? null
    : i18n.t('FORM_VALIDATION.INVALID_DISCORD_INVITE_LINK');
};

export const validateNotEmptyArray = (value: unknown[]) => {
  return value.length > 0 ? null : i18n.t('FORM_VALIDATION.REQUIRED');
};

export const validateMaxLengthArray = (value: unknown[], maxLength: number) => {
  return value.length > maxLength ? i18n.t('FORM_VALIDATION.MAX_ARRAY_LENGTH', { MAX_LENGTH: maxLength }) : null;
};

export const validateLeadInfoToCollect = (values: string[]) => {
  return (
    values.length > 0 &&
    values.every((value) =>
      Object.values<string>(InfoType).includes(value) ? null : i18n.t('FORM_VALIDATION.INVALID_LEAD_INFO_TYPE')
    )
  );
};

export const validateEmailHandoffInfoToCollect = (values: string[]) => {
  // Email field is always required
  if (!values.includes(InfoType.EMAIL)) return i18n.t('FORM_VALIDATION.REQUIERED_EMAIL_HANDOFF_FIELDS');
  return (
    values.length > 0 &&
    values.every((value) =>
      Object.values<string>(InfoType).includes(value) ? null : i18n.t('FORM_VALIDATION.INVALID_EMAIL_HANDOFF_INFO_TYPE')
    )
  );
};

export const validateOpenRouterAPIKey = (apiKey: string) => {
  const OPEN_ROUTER_API_KEY_REGEX = /^sk-[a-zA-Z0-9_-]+$/;
  return OPEN_ROUTER_API_KEY_REGEX.test(apiKey) ? null : i18n.t('FORM_VALIDATION.INVALID_OPEN_ROUTER_API_KEY');
};

export const voiceConfigurationValidator = (config: VoiceConfiguration): string | null => {
  if (!config.name || !config.voiceId || !config.provider || !config.language || !config.gender || !config.age) {
    return i18n.t('FORM_VALIDATION.VOICE_CONFIGURATION.INVALID');
  }
  return null;
};

export const transcriptionConfigurationValidator = (config: TranscriptionConfiguration): string | null => {
  if (!config.provider) return i18n.t('FORM_VALIDATION.TRANSCRIPTION_CONFIGURATION.INVALID');
  return null;
};

export const validateIsPhoneNumber = (value: string): string | null => {
  // Regular expression for E.164 format https://en.wikipedia.org/wiki/E.164
  const e164Regex = /^\+[1-9]\d{1,14}$/;

  if (e164Regex.test(value)) {
    return null;
  } else {
    return i18n.t('FORM_VALIDATION.INVALID_PHONE_NUMBER');
  }
};

export const noSpacesValidator = (value: string): string | null => {
  if (value.includes(' ')) {
    return i18n.t('FORM_VALIDATION.NO_SPACES_ALLOWED');
  }
  return null;
};
export const validatePropertyKeyRequired = (key: string): string | null => {
  if (!key.trim()) {
    return i18n.t('FORM_VALIDATION.JSON_SCHEMA_BUILDER.PROPERTY_NAME_REQUIRED');
  }
  return null;
};

export const validatePropertyKeyNoSpaces = (key: string): string | null => {
  if (key.includes(' ')) {
    return i18n.t('FORM_VALIDATION.JSON_SCHEMA_BUILDER.PROPERTY_NAME_CANNOT_CONTAIN_SPACES');
  }
  return null;
};

export const validatePropertyKeyFormat = (key: string): string | null => {
  if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(key)) {
    return i18n.t('FORM_VALIDATION.JSON_SCHEMA_BUILDER.PROPERTY_NAME_MUST_CONTAIN_ONLY_LETTERS_NUMBERS_UNDERSCORES');
  }
  return null;
};

export const validatePropertyKeyUnique = (key: string, existingKeys: string[]): string | null => {
  if (existingKeys.includes(key)) {
    return i18n.t('FORM_VALIDATION.JSON_SCHEMA_BUILDER.PROPERTY_NAME_ALREADY_EXISTS');
  }
  return null;
};

export const validatePropertyKey = (key: string, existingKeys: string[]): string | null => {
  return (
    validatePropertyKeyRequired(key) ||
    validatePropertyKeyNoSpaces(key) ||
    validatePropertyKeyFormat(key) ||
    validatePropertyKeyUnique(key, existingKeys)
  );
};
